/* eslint-disable indent */

import { MouseEventHandler, useContext, useEffect, useState, VoidFunctionComponent } from 'react'
import { useCountdown } from 'rooks'
import ms from 'ms'
import axios, { AxiosError } from 'axios'
import { useHistory } from 'react-router-dom'
import Button from 'components/button'
import { Crown } from 'theme/profile'
import ErrorView from 'components/error-view'
import textServices from 'services/text-services'
import { useSession } from '../../../contexts/session-context'
import { useTrivia } from '../../../contexts/trivia-context'
import { Trivia } from '../../../contexts/trivia-context/reducer'
import { LoaderContext } from '../../../contexts/loader-context'
import {
  DivSelector,
  LiSelectorOption,
  DivSelectOption,
  DivTitleTriviaList,
  H2TitleTriviaList,
  QuestionHeader,
  QuestionNumber,
  QuestionTitle
} from '../../../theme/trivias'
import Congrats from './congrats'

const OptionSelector: VoidFunctionComponent<{ trivia: Trivia }> = ({ trivia }) => {
  const [{ user }] = useSession()
  const history = useHistory()
  const [question, setQuestion] = useState(1)
  const [selected, setSelected] = useState<null | number>(0) // Se default value to 0
  const [showCongrats, setShowCongrats] = useState(false)
  const { load, setLoad } = useContext(LoaderContext)
  const [state, { validateTrivia, resetTrivia, exchangePrize, setError }] = useTrivia()

  const { question: q } = trivia

  // TODO: use timeinseconds
  const [end, setEnd] = useState(new Date(Date.now() + ms(`${trivia.timeInSeconds}s`)))
  const countdown = useCountdown(end, { interval: 1000 })

  const selectOption: MouseEventHandler<HTMLLIElement> = (event) => {
    gtag('event', 'Click', {
      event_category: GTM_EVENT_CATEGORY,
      event_label: 'TRIVIA - Seleccionar Opcion',
      value: '10'
    })
    const { currentTarget } = event

    const id = currentTarget.getAttribute('data-id') as string

    setSelected(parseInt(id, 10))
  }

  useEffect(() => {
    if ((!countdown || selected) && !state.prize && !state.error) {
      setSelected(0)

      setTimeout(
        async () => {
          setLoad(true)
          try {
            const shouldSetNext = await validateTrivia(trivia.id, q.questionId, selected as number)

            if (shouldSetNext) {
              //  Set in 0 to force the update of status InProcess to Fail in case that no option will selected in next question
              setQuestion((q) => q + 1)
              setShowCongrats(true)
              setTimeout(() => {
                setShowCongrats(false)
                setEnd(new Date(Date.now() + ms(`${trivia.timeInSeconds}s`)))
              }, 1000)
            }
          } catch (error) {
            console.error(error)

            if (axios.isAxiosError(error)) {
              const err = error as AxiosError
              setError(err)
            }
          } finally {
            setLoad(false)
          }
        },
        selected ? 0 : 1000
      )
    }
  }, [countdown, selected])

  useEffect(() => {
    return resetTrivia
  }, [])

  const handleRetry = () => {
    const event = new CustomEvent('background-color', {
      detail: {
        shouldUseWhiteBg: true,
        from: 'trivia error close'
      }
    })
    gtag('event', 'Click', {
      event_category: GTM_EVENT_CATEGORY,
      event_label: 'TRIVIA - Vuelva a Intentarlo',
      value: '10'
    })
    window.dispatchEvent(event)
    resetTrivia()
    history.push('/trivias')
  }

  if (state?.error) {
    return <ErrorView code={state?.error.response?.data.code ?? 9999} buttonMenu={handleRetry} onRetry={handleRetry} />
  }

  if (state.prize) {
    const event = new CustomEvent('background-color', {
      detail: {
        shouldUseWhiteBg: false,
        from: 'trivias prize show'
      }
    })
    window.dispatchEvent(event)
    return (
      <div className="flex-1 space-y-4 flex flex-col p-8 justify-between w-full sm:max-w-sm">
        <div>
          <h1 className="uppercase text-bold text-3xl text-center font-bold">¡Veeeeenga {user?.firstName}!</h1>
          <p className="text-center uppercase text-2xl din">
            Ganaste <b>{state.prize.title}</b>
          </p>
        </div>
        {state.prize.imageUrl && <img src={state.prize.imageUrl} alt={state.prize.title} />}

        {!state.prize.imageUrl && (
          <Crown
            className="w-48 h-48 flex justify-center items-center mx-auto text-center"
            style={{ backgroundSize: 'contain' }}
          >
            <p className="text-4xl font-bold mx-auto">{state.prize.title.replace('', '')}</p>
          </Crown>
        )}
        {state.prize.stickerId > 0 ? (
          <Button
            onClick={() => {
              gtag('event', 'Click', {
                event_category: GTM_EVENT_CATEGORY,
                event_label: 'TRIVIA - Ver mis Premios',
                value: '10'
              })
              history.push(textServices.findByText('trivias/exchange/button/url', '/collections'))
            }}
          >
            <p>{textServices.findByText('trivias/exchange/button/text', 'VER MIS SELLOS')}</p>
          </Button>
        ) : (
          <Button
            onClick={() => {
              gtag('event', 'Click', {
                event_category: GTM_EVENT_CATEGORY,
                event_label: 'TRIVIA - Ver mis Premios',
                value: '10'
              })
              exchangePrize(state.prize?.points || 0)
            }}
          >
            <p>{textServices.findByText('trivias/exchange/button/text', 'VER MIS PREMIOS')}</p>
          </Button>
        )}
      </div>
    )
  }

  if (showCongrats) {
    return <Congrats item={state?.prize?.title} />
  }

  if (load) {
    return <></>
  }

  if (q.options.length === 2 && q.options[0].imageUrl !== '' && q.options[1].imageUrl !== '') {
    return (
      <DivSelector className="overflow-hidden w-full flex flex-col">
        <QuestionHeader className={` flex justify-start items-center`}>
          <QuestionNumber className="uppercase text-lg py-4 pl-6 pr-4">
            {question}/{q.options.length}
          </QuestionNumber>
          <QuestionTitle className="uppercase text-sm py-4 pl-4 pr-6">{trivia.title}</QuestionTitle>
        </QuestionHeader>

        <div className="flex flex-col gap-4 mx-5 mt-10">
          {q.options.map((option) => {
            const isSelected = option.optionId === selected
            return (
              <div
                isSelected={isSelected}
                key={`option-${q.question.replace(/-/g, '')}-${option.optionId}`}
                data-id={option.optionId}
                // @ts-ignore
                onClick={selectOption}
                className="w-[325px] h-[160px]"
              >
                <img
                  src={option.imageUrl ?? `${AZURE_BASE_URL}/default.svg`}
                  alt="img"
                  className="w-full h-[130.75px] rounded-tl-lg rounded-tr-lg"
                />
                <div className="h-[30.11px] w-full rounded-bl-lg rounded-br-lg" style={{ background: '#ED9C53' }}>
                  <p className="text-black text-center rounded-bl-lg rounded-br-lg">{option.option}</p>
                </div>
              </div>
            )
          })}
        </div>

        <div className="flex flex-1 justify-center items-center">
          <div
            className="relative w-20 h-20 z-10"
            style={{
              backgroundImage: `url(${AZURE_BASE_URL}/timer.svg})`,
              backgroundSize: 'contain'
            }}
          >
            <p
              className="absolute top-1/2 left-1/2 text-2xl font-bold text-center"
              style={{ transform: 'translate(-50%, -50%)' }}
            >
              {countdown}
            </p>
          </div>
        </div>
      </DivSelector>
    )
  } else if (
    q.options.length >= 3 &&
    q.options[0].imageUrl !== '' &&
    q.options[1].imageUrl !== '' &&
    q.options[2].imageUrl !== '' &&
    q.options[3].imageUrl !== ''
  ) {
    return (
      <DivSelector className="overflow-hidden w-full flex flex-col">
        <QuestionHeader className={` flex justify-start items-center`}>
          <QuestionNumber className="uppercase text-lg py-4 pl-6 pr-4 nersans">
            {question}/{q.options.length}
          </QuestionNumber>
          <QuestionTitle className="uppercase text-sm py-4 pl-4 pr-6 nersans">{trivia.title}</QuestionTitle>
        </QuestionHeader>

        <div className="grid grid-cols-2 gap-4 place-items-center mx-5 mt-5">
          {q.options.map((option) => {
            const isSelected = option.optionId === selected
            return (
              <div
                isSelected={isSelected}
                key={`option-${q.question.replace(/-/g, '')}-${option.optionId}`}
                data-id={option.optionId}
                // @ts-ignore
                onClick={selectOption}
                className="w-[152px] h-[186px]"
              >
                <img
                  src={option.imageUrl ?? `${AZURE_BASE_URL}/default.svg`}
                  alt="img"
                  className="w-full h-[152px] rounded-tl-lg rounded-tr-lg"
                />
                <div className="h-[35px] w-full rounded-bl-lg rounded-br-lg" style={{ background: '#ED9C53' }}>
                  <p className="text-black text-center rounded-bl-lg rounded-br-lg">{option.option}</p>
                </div>
              </div>
            )
          })}
        </div>
        <div className="flex flex-1 justify-center items-center">
          <div
            className="relative w-20 h-20 z-10"
            style={{
              backgroundImage: `url('${AZURE_BASE_URL}/timer.svg')`,
              backgroundSize: 'contain'
            }}
          >
            <p
              className="absolute top-1/2 left-1/2 text-2xl font-bold text-center"
              style={{ transform: 'translate(-50%, -50%)' }}
            >
              {countdown}
            </p>
          </div>
        </div>
      </DivSelector>
    )
  } else {
    return (
      <DivSelector className="overflow-y-scroll w-full flex flex-col">
        <DivTitleTriviaList className={` flex justify-start items-center`}>
          <H2TitleTriviaList className="font-bold uppercase text-lg py-4 pl-6 pr-4">
            {trivia.interest}
          </H2TitleTriviaList>
          <h2 className="uppercase text-sm py-4 pl-4 pr-6">{trivia.title}</h2>
        </DivTitleTriviaList>
        <div className="px-8 py-4 din">
          <span>
            <b>{question})</b> {q.question}
          </span>
        </div>
        <ul>
          {q.options.map((option) => {
            const isSelected = option.optionId === selected
            return (
              <LiSelectorOption
                className={`h-16 px-8 flex items-center border-t transition-colors`}
                isSelected={isSelected}
                key={`option-${q.question.replace(/-/g, '')}-${option.optionId}`}
                data-id={option.optionId}
                onClick={selectOption}
              >
                <div
                  className={`w-6 h-6 border mr-6 circle rounded-full transition-all border-black flex justify-center items-center ${isSelected ? '' : 'opacity-40'
                    }`}
                >
                  <DivSelectOption className={`w-4 h-4 circle rounded-full transition-all`} isSelected={isSelected} />
                </div>
                <p>{option.option}</p>
              </LiSelectorOption>
            )
          })}
        </ul>

        <div className="flex flex-1 justify-center items-center">
          <div className="relative w-20 h-20 z-10">
            <Crown className="w-20 h-20 flex justify-center items-center" style={{ backgroundSize: 'contain' }}></Crown>
            <p
              className="absolute top-1/2 left-1/2 text-2xl font-bold text-center"
              style={{ transform: 'translate(-50%, -50%)' }}
            >
              {countdown}
            </p>
          </div>
        </div>
      </DivSelector>
    )
  }
}

export default OptionSelector
